import { useEffect, useState } from 'react'
import { Alert, App as AntdApp, Button } from 'antd'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import apiClient from '../../api/client'
import { attachStoredReferral } from '../../utils/referralCapture'
import { grantConsent } from '../../utils/gtmBoot'
import PortalHeader from './PortalHeader'
import PortalSidebar from './PortalSidebar'

const PAGE_TITLE_KEYS: Record<string, { key: string; fallback: string }> = {
  '/portal': { key: 'portal.nav.dashboard', fallback: 'Dashboard' },
  '/portal/services': { key: 'portal.nav.services', fallback: 'Services' },
  '/portal/billing': { key: 'portal.nav.billing', fallback: 'Finanzen' },
  '/portal/tickets': { key: 'portal.nav.tickets', fallback: 'Tickets' },
  '/portal/datenschutz': { key: 'portal.nav.datenschutz', fallback: 'Datenschutz' },
  '/portal/orders': { key: 'portal.nav.orders', fallback: 'Bestellhistorie' },
  '/portal/quotes': { key: 'portal.nav.quotes', fallback: 'Angebote' },
  '/portal/profile': { key: 'portal.nav.profile', fallback: 'Mein Profil' },
  '/portal/api': { key: 'portal.nav.api', fallback: 'API-Zugang' },
  '/portal/monitoring': { key: 'portal.nav.monitoring', fallback: 'Monitoring' },
  '/portal/notifications': { key: 'portal.nav.notifications', fallback: 'Benachrichtigungen' },
}

interface PortalMe {
  id: number
  email: string
  email_verified_at?: string | null
}

export default function PortalLayout() {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const { message } = AntdApp.useApp()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [me, setMe] = useState<PortalMe | null>(null)
  const [resending, setResending] = useState(false)

  useEffect(() => {
    apiClient
      .get<{ data: PortalMe }>('/api/v1/portal/me')
      .then((res) => {
        setMe(res.data.data)
        attachStoredReferral()
        // Authenticated B2B portal user → auto-grant analytics consent
        // (legitimate interest), so no cookie banner is needed for logged-in users.
        grantConsent()
      })
      .catch(() => navigate('/portal/login'))
  }, [navigate])

  const resend = async () => {
    if (!me) return
    setResending(true)
    try {
      await apiClient.post('/api/v1/portal/resend-verification', { email: me.email })
      void message.success(t('portal.verify.resendOk',
        'Bestätigungs-Mail wurde erneut versendet.'))
    } catch {
      void message.error(t('portal.verify.resendFailed', 'Versand fehlgeschlagen'))
    } finally {
      setResending(false)
    }
  }

  useEffect(() => {
    setSidebarOpen(false)
  }, [location.pathname])

  const title = PAGE_TITLE_KEYS[location.pathname]
  const pageTitle = title ? t(title.key, title.fallback) : undefined

  return (
    <div className="app-layout">
      {sidebarOpen && (
        <div
          className="sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}
      <PortalSidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <main className="main-content">
        <PortalHeader pageTitle={pageTitle} onMenuToggle={() => setSidebarOpen((p) => !p)} />
        {me && !me.email_verified_at && (
          <Alert
            type="warning"
            showIcon
            banner
            message={t('portal.verify.banner',
              'Bitte bestätigen Sie Ihre E-Mail-Adresse. Wir haben Ihnen einen Bestätigungs-Link an {{email}} geschickt.',
              { email: me.email })}
            action={
              <Button size="small" loading={resending} onClick={resend}>
                {t('portal.verify.resend', 'Erneut senden')}
              </Button>
            }
          />
        )}
        <div className="content-area">
          <Outlet />
        </div>
      </main>
    </div>
  )
}
